import { useState } from "react";
import { Link } from "react-router-dom";
import { Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { WidgetContentSkeleton } from "@/components/shared/loading-skeleton";
import { AuditLogActorLabel } from "@/features/audit-logs/components/AuditLogActorLabel";
import { AuditLogDetailSheet } from "@/features/audit-logs/components/AuditLogDetailSheet";
import {
  auditLogSummary,
  auditLogWidgetResourceLabel,
} from "@/features/audit-logs/lib/audit-log-display";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { ScrollText, ArrowLeft } from "lucide-react";
import type { AuditLog } from "@/types/audit-log";
import type { PaginatedResult } from "@/types/common";

interface RecentActivityWidgetProps {
  data: PaginatedResult<AuditLog> | undefined;
  isLoading: boolean;
}

export function RecentActivityWidget({
  data,
  isLoading,
}: RecentActivityWidgetProps) {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const items = data?.items ?? [];

  function openDetail(log: AuditLog) {
    setSelectedLog(log);
    setDetailOpen(true);
  }

  return (
    <>
      <Card className="flex flex-col overflow-hidden" dir="rtl">
        <div className="accent-top-bar" />
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">آخر الإجراءات</CardTitle>
            <div className="rounded-md bg-accent/10 p-1.5 text-accent">
              <ScrollText className="size-4" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 space-y-1">
          {isLoading ? (
            <WidgetContentSkeleton rows={2} />
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              لا توجد إجراءات حديثة
            </p>
          ) : (
            items.map((entry) => {
              const { actionLabel } = auditLogSummary(entry);
              const resourceLabel = auditLogWidgetResourceLabel(entry);
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => openDetail(entry)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border-b border-border/60 py-2 text-sm text-right transition-colors last:border-0 hover:bg-muted/40"
                >
                  <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                    <span className="truncate font-medium text-foreground">
                      {actionLabel}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {entry.userId ? (
                        <AuditLogActorLabel userId={entry.userId} />
                      ) : (
                        resourceLabel ?? "—"
                      )}
                      {entry.userId && resourceLabel ? (
                        <>
                          {" · "}
                          {resourceLabel}
                        </>
                      ) : null}
                    </span>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatOptionalArabicDateTime(entry.createdAt)}
                  </span>
                </button>
              );
            })
          )}
        </CardContent>

        <CardFooter className="justify-start pt-0">
          <Link
            to={appPath(Routes.AUDIT_LOGS)}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
          >
            عرض سجل التدقيق
            <ArrowLeft className="size-4" />
          </Link>
        </CardFooter>
      </Card>

      <AuditLogDetailSheet
        logId={selectedLog?.id ?? null}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        preview={selectedLog ?? undefined}
      />
    </>
  );
}
